/**
 * A nudge for a client whose phone number was never proven.
 *
 * Phone verification became part of onboarding in 0076, which leaves everyone who
 * joined before it with a number nobody has ever proved answers. That number is
 * what an agent calls to confirm an unusual instruction, so an unproven one is a
 * gap in exactly the check that matters most.
 *
 * A nudge and not a block: these clients did nothing wrong, their number was
 * simply never asked for, and locking them out of their own money over our
 * change of process would be the wrong way round. Same shape as the RFC
 * reminder, which is the established answer here for "required later, asked for
 * now".
 *
 * Silent while the status is unknown. A banner that appears and then vanishes on
 * every load is worse than one that arrives a moment late.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import authService from '../../services/auth.service';
import { colors } from '../../styles/theme';

export default function PhoneUnprovenBanner(): JSX.Element | null {
  const [unproven, setUnproven] = useState(false);

  useEffect(() => {
    let live = true;
    authService
      .getPhoneVerification()
      .then((status) => {
        // No number at all is onboarding's business, not this banner's: the
        // person has nothing to confirm yet.
        if (live) setUnproven(status.phone_masked !== null && !status.verified);
      })
      // A nudge is never worth an error state. If we cannot tell, we say nothing.
      .catch(() => undefined);
    return () => { live = false; };
  }, []);

  if (!unproven) return null;

  return (
    <aside
      role="status"
      aria-live="polite"
      className="rounded-2xl p-4 mt-5 flex items-start justify-between gap-4 flex-wrap"
      style={{ background: colors.warningBg, border: '1px solid #FCD34D', color: colors.inkSoft }}
    >
      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold uppercase tracking-widest mb-1"
           style={{ color: colors.warning }}>
          Confirm your phone number
        </p>
        <p className="text-sm" style={{ color: colors.ink }}>
          We have a number for you, but it has never been confirmed. Confirming it lets us
          reach you if we ever need to check something before your money moves. It takes a
          minute and nothing is blocked in the meantime.
        </p>
      </div>
      <Link
        to="/profile"
        className="text-sm font-semibold px-4 py-2 rounded-lg whitespace-nowrap"
        style={{ background: colors.navy, color: '#FFFFFF' }}
      >
        Confirm it
      </Link>
    </aside>
  );
}
